import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faXmark,
  faEye,
  faTriangleExclamation,
  faInbox,
  faTrophy,
  faCircleCheck,
  faCircleHalfStroke,
  faCircleXmark,
  faUserCheck,
  faHourglassHalf,
  faEllipsis,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Panel, Segmented, DashModal } from './Dashui';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const TYPES = [
  ['all', 'All'],
  ['upcoming', 'Upcoming'],
  ['demo', 'Demo'],
  ['paid', 'Paid'],
];

// Same colour rules as before for the "Report" column
const reportTone = (status) => {
  if (!status) return 'red';
  const s = status.toLowerCase();
  if (s.includes('full pay') || s.includes('enrolled')) return 'green';
  if (s.includes('half pay') || s.includes('postponed')) return 'orange';
  if (s.includes('pending')) return 'blue';
  if (s.includes('awaiting')) return 'grey';
  return 'red';
};

const completionTone = (status = '') => {
  const s = status.toLowerCase();
  if (s.includes('completed')) return 'green';
  if (s.includes('scheduled')) return 'blue';
  return 'orange';
};

const isCompleted = (s) => s.ClassCompletionStatus?.toLowerCase().includes('completed');

const fmt = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).replace(/,/g, ''),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
  };
};

const waLink = (phone) =>
  `https://api.whatsapp.com/send/?phone=${String(phone).replace(/[^\d]/g, '')}&type=phone_number&app_absent=0`;

/* ======================================================================
   Demo details editor (same endpoints + same payload as before)
   ====================================================================== */
const DemoModal = ({ demoId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/dashboard/demosubmit/${demoId}`, auth());
        if (!cancelled) setD(res.data);
      } catch (err) {
        console.error('Error fetching demo details:', err);
        if (!cancelled) setError(err.response?.data?.message || 'We couldn’t load this demo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [demoId, reload]);

  const set = (patch) => setD((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `${API}/api/admin/dashboard/demosubmit/${d.id}`,
        {
          projectId: d.projectId,
          rating: d.rating,
          parentsAvailable: d.parentsContacted,
          classStatus: d.classStatus,
          details: d.details,
          classReport: d.classReport,
        },
        auth()
      );
      onChanged();
      onClose();
    } catch (err) {
      console.error('Error updating demo:', err);
      setError(err.response?.data?.message || 'We couldn’t save the changes.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.delete(`${API}/api/admin/dashboard/demosubmit/${d.id}`, auth());
      onChanged();
      onClose();
    } catch (err) {
      console.error('Error deleting demo:', err);
      setError(err.response?.data?.message || 'We couldn’t delete this demo.');
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const when = d ? fmt(d.classDateTime) : null;
  const parents = d && (d.parentsContacted === true || String(d.parentsContacted) === '1') ? '1' : '0';

  const footer = d ? (
    confirmDelete ? (
      <>
        <span className="rmadd-confirm-text">Delete this demo for good?</span>
        <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={() => setConfirmDelete(false)} disabled={saving}>
          Keep it
        </button>
        <button type="button" className="rmadd-btn rmadd-btn--danger" onClick={remove} disabled={saving}>
          {saving ? 'Deleting…' : 'Yes, delete'}
        </button>
      </>
    ) : (
      <>
        <button type="button" className="rmadd-btn rmadd-btn--text-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
          Delete
        </button>
        <span className="rmadd-grow" />
        <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="rmadd-btn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </>
    )
  ) : null;

  return (
    <DashModal title={d?.studentName || 'Demo class'} kicker="Demo class" onClose={onClose} busy={saving} footer={footer}>
      {loading ? (
        <div className="rmadd-skel rmadd-skel--form" aria-busy="true" />
      ) : !d ? (
        <div className="rmadd-state">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      ) : (
        <div className="rmadd-form">
          <p className="rmadd-form-when">
            {when.date} · {when.time}
          </p>

          <label className="rmadd-field">
            <span>Project</span>
            <select className="rmadd-input" value={d.projectId || ''} onChange={(e) => set({ projectId: parseInt(e.target.value, 10) })}>
              <option value="">Select a project</option>
              {(d.projects || []).map((p) => (
                <option key={p.Id} value={p.Id}>
                  {p.ProjectName}
                </option>
              ))}
            </select>
          </label>

          <div className="rmadd-form-row">
            <label className="rmadd-field">
              <span>Class status</span>
              <select className="rmadd-input" value={d.classStatus ?? ''} onChange={(e) => set({ classStatus: e.target.value })}>
                <option value="" disabled>
                  Select status
                </option>
                <option value="0">Class completed</option>
                <option value="1">Student not joined</option>
                <option value="2">Student left</option>
                <option value="3">Student not interested</option>
              </select>
            </label>

            <label className="rmadd-field">
              <span>Class report</span>
              <select className="rmadd-input" value={d.classReport || 0} onChange={(e) => set({ classReport: parseInt(e.target.value, 10) })}>
                <option value="0">Awaiting</option>
                <option value="1">Enrolled</option>
                <option value="2">Pending</option>
                <option value="3">Closed</option>
                <option value="4">Postponed</option>
                <option value="5">Not interested</option>
              </select>
            </label>
          </div>

          <div className="rmadd-form-row">
            <label className="rmadd-field">
              <span>Rating</span>
              <select className="rmadd-input" value={d.rating ?? '0'} onChange={(e) => set({ rating: e.target.value })}>
                <option value="0">No rating</option>
                <option value="1">1 – Poor</option>
                <option value="2">2 – Fair</option>
                <option value="3">3 – Good</option>
                <option value="4">4 – Very good</option>
                <option value="5">5 – Excellent</option>
              </select>
            </label>

            <label className="rmadd-field">
              <span>Talked with parents</span>
              <select className="rmadd-input" value={parents} onChange={(e) => set({ parentsContacted: e.target.value })}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </label>
          </div>

          <label className="rmadd-field">
            <span>Teacher notes</span>
            <textarea className="rmadd-input rmadd-textarea" rows={4} value={d.details || ''} onChange={(e) => set({ details: e.target.value })} />
          </label>

          {error && (
            <p className="rmadd-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </DashModal>
  );
};

/* ======================================================================
   Overview tab: the selected month
   ====================================================================== */
const DashOverview = ({ userData }) => {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [demoId, setDemoId] = useState(null);
  const [showAllTeachers, setShowAllTeachers] = useState(false);

  const isCurrentMonth = ym.y === now.getFullYear() && ym.m === now.getMonth();
  const monthName = new Date(ym.y, ym.m).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const shiftMonth = (delta) =>
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      const t = new Date();
      if (d.getFullYear() * 12 + d.getMonth() > t.getFullYear() * 12 + t.getMonth()) return { y, m };
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  // ---------- Month statistics (same endpoint) ----------
  useEffect(() => {
    if (!userData?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/dashboard/statistics/${ym.m + 1}/${ym.y}`, auth());
        if (!cancelled) setData(res.data || {});
      } catch (err) {
        console.error('API Error:', err);
        if (!cancelled) setError(err.response?.data?.message || 'We couldn’t load this month.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.id, ym, reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  // ---------- Derived numbers (same formulas as before) ----------
  const sessions = useMemo(() => data?.allSessions || [], [data]);
  const paid = { full: data?.fullPayCount || 0, half: data?.halfPayCount || 0, none: data?.noPayCount || 0 };
  const demo = {
    enrolled: data?.enrolledStudents || 0,
    pending: data?.pendingStudents || 0,
    other:
      (data?.registrationPostponedStudents || 0) +
      (data?.notInterestedCount || 0) +
      (data?.awaiting || 0) +
      (data?.caseClosedStudents || 0),
  };
  const completedDemos = sessions.filter((s) => s.studenttype === 'demo' && isCompleted(s)).length;
  const conversion = completedDemos > 0 ? Math.round((demo.enrolled / completedDemos) * 100) : 0;

  const teachers = useMemo(() => {
    const map = {};
    sessions
      .filter((s) => s.studenttype === 'paid' && isCompleted(s))
      .forEach((s) => {
        const name = s.TeacherFullName || 'Unknown teacher';
        const id = s.TeacherId || name;
        if (!map[id]) map[id] = { id, name, count: 0 };
        map[id].count += 1;
      });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [sessions]);
  const teacherTotal = teachers.reduce((t, x) => t + x.count, 0);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter(
      (s) => (type === 'all' || s.studenttype === type) && (!q || (s.StudentFullName || '').toLowerCase().includes(q))
    );
  }, [sessions, search, type]);

  const typeCounts = useMemo(() => {
    const c = { all: sessions.length, upcoming: 0, demo: 0, paid: 0 };
    sessions.forEach((s) => {
      if (c[s.studenttype] !== undefined) c[s.studenttype] += 1;
    });
    return c;
  }, [sessions]);

  const medal = ['is-gold', 'is-silver', 'is-bronze'];

  return (
    <>
      {/* ---------- Month bar ---------- */}
      <div className="rmadd-monthbar">
        <div className="rmadd-month">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <strong>{monthName}</strong>
          <button type="button" onClick={() => shiftMonth(1)} disabled={isCurrentMonth} aria-label="Next month">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
        {!isCurrentMonth && (
          <button type="button" className="rmadd-btn rmadd-btn--ghost rmadd-btn--sm" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() })}>
            This month
          </button>
        )}
      </div>

      {error ? (
        <div className="rmadd-state rmadd-state--box">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmadd-btn" onClick={refresh}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {/* ---------- KPIs ---------- */}
          <div className={`rmadd-kpis ${loading ? 'is-loading' : ''}`}>
            <div className="rmadd-kpi">
              <span className="rmadd-kpi-label">Paid classes</span>
              <strong className="rmadd-kpi-big">{loading ? '–' : paid.full + paid.half + paid.none}</strong>
              <ul className="rmadd-kpi-split">
                <li className="tone-green">
                  <FontAwesomeIcon icon={faCircleCheck} /> Full <b>{loading ? '–' : paid.full}</b>
                </li>
                <li className="tone-orange">
                  <FontAwesomeIcon icon={faCircleHalfStroke} /> Half <b>{loading ? '–' : paid.half}</b>
                </li>
                <li className="tone-red">
                  <FontAwesomeIcon icon={faCircleXmark} /> No pay <b>{loading ? '–' : paid.none}</b>
                </li>
              </ul>
            </div>

            <div className="rmadd-kpi">
              <span className="rmadd-kpi-label">Trial results</span>
              <strong className="rmadd-kpi-big">{loading ? '–' : demo.enrolled + demo.pending + demo.other}</strong>
              <ul className="rmadd-kpi-split">
                <li className="tone-green">
                  <FontAwesomeIcon icon={faUserCheck} /> Enrolled <b>{loading ? '–' : demo.enrolled}</b>
                </li>
                <li className="tone-blue">
                  <FontAwesomeIcon icon={faHourglassHalf} /> Pending <b>{loading ? '–' : demo.pending}</b>
                </li>
                <li className="tone-grey">
                  <FontAwesomeIcon icon={faEllipsis} /> Other <b>{loading ? '–' : demo.other}</b>
                </li>
              </ul>
            </div>

            <div className="rmadd-kpi rmadd-kpi--accent">
              <span className="rmadd-kpi-label">Trial → enrolled</span>
              <strong className="rmadd-kpi-big">{loading ? '–' : `${conversion}%`}</strong>
              <p className="rmadd-kpi-note">
                {loading ? 'Loading…' : `${demo.enrolled} enrolled from ${completedDemos} completed trial${completedDemos === 1 ? '' : 's'}`}
              </p>
              <span className="rmadd-kpi-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(conversion, 100)}%` }} />
              </span>
            </div>
          </div>

          {/* ---------- Top teachers ---------- */}
          <Panel
            title="Top teachers"
            sub="Completed paid classes this month"
            actions={
              teachers.length > 3 && (
                <button type="button" className="rmadd-link" onClick={() => setShowAllTeachers(true)}>
                  View all ({teachers.length})
                </button>
              )
            }
          >
            {loading ? (
              <div className="rmadd-skel rmadd-skel--row" />
            ) : teachers.length === 0 ? (
              <p className="rmadd-none">No completed paid classes this month yet.</p>
            ) : (
              <ol className="rmadd-teachers">
                {teachers.slice(0, 3).map((t, i) => (
                  <li key={t.id} className="rmadd-teacher">
                    <span className={`rmadd-rank ${medal[i]}`}>
                      {i === 0 ? <FontAwesomeIcon icon={faTrophy} /> : i + 1}
                    </span>
                    <div className="rmadd-teacher-body">
                      <strong>{t.name}</strong>
                      <span>
                        {t.count} class{t.count === 1 ? '' : 'es'} · {teacherTotal ? Math.round((t.count / teacherTotal) * 100) : 0}%
                      </span>
                      <span className="rmadd-share" aria-hidden="true">
                        <span style={{ width: `${teacherTotal ? (t.count / teacherTotal) * 100 : 0}%` }} />
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>

          {/* ---------- Sessions ---------- */}
          <Panel title="Class sessions" sub={monthName} className="rmadd-panel--flush">
            <div className="rmadd-toolbar">
              <label className="rmadd-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <span className="rmadd-sr">Search students</span>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search students" />
                {search && (
                  <button type="button" onClick={() => setSearch('')} aria-label="Clear search">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </label>
              <Segmented
                label="Class type"
                value={type}
                onChange={setType}
                options={TYPES.map(([v, l]) => [v, `${l}${loading ? '' : ` ${typeCounts[v]}`}`])}
              />
            </div>

            {loading ? (
              <div className="rmadd-list-skel">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="rmadd-skel rmadd-skel--line" />
                ))}
              </div>
            ) : shown.length === 0 ? (
              <div className="rmadd-state">
                <FontAwesomeIcon icon={faInbox} />
                <p>{sessions.length ? 'No classes match your search.' : 'No classes this month.'}</p>
              </div>
            ) : (
              <div className="rmadd-table-wrap">
                <table className="rmadd-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Student</th>
                      <th>Teacher</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Report</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((s, i) => {
                      const w = fmt(s.ClassDateTime);
                      return (
                        <tr key={`${s.DemoId || s.IdUser}-${s.ClassDateTime}-${i}`}>
                          <td data-label="Type">
                            <span className={`rmadd-type is-${s.studenttype}`}>{s.studenttype}</span>
                          </td>
                          <td data-label="Student" className="rmadd-td-student">
                            <a href={`/Admin/studentprofile/${s.IdUser}`} target="_blank" rel="noopener noreferrer">
                              {s.StudentFullName || 'Student'}
                            </a>
                          </td>
                          <td data-label="Teacher" className="rmadd-td-muted">
                            {s.TeacherFullName || '—'}
                          </td>
                          <td data-label="Date" className="rmadd-td-date">
                            {w.date}
                            <small>{w.time}</small>
                          </td>
                          <td data-label="Status">
                            <span className={`rmadd-pill tone-${completionTone(s.ClassCompletionStatus)}`}>{s.ClassCompletionStatus || '—'}</span>
                          </td>
                          <td data-label="Report">
                            <span className={`rmadd-pill tone-${reportTone(s.ClassStatus)}`}>{s.ClassStatus || '—'}</span>
                          </td>
                          <td className="rmadd-td-actions">
                            {s.studenttype !== 'paid' && s.MobileNumber && (
                              <a className="rmadd-icon-btn is-wa" href={waLink(s.MobileNumber)} target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${s.StudentFullName || ''}`}>
                                <FontAwesomeIcon icon={faWhatsapp} />
                              </a>
                            )}
                            {s.studenttype === 'demo' && s.DemoId && (
                              <button type="button" className="rmadd-icon-btn" onClick={() => setDemoId(s.DemoId)} aria-label="Demo details">
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}

      {/* ---------- All teachers ---------- */}
      {showAllTeachers && (
        <DashModal title="All teachers" kicker={monthName} wide onClose={() => setShowAllTeachers(false)}>
          <ul className="rmadd-mini-stats">
            <li>
              <span>Teachers</span>
              <strong>{teachers.length}</strong>
            </li>
            <li>
              <span>Paid classes</span>
              <strong>{teacherTotal}</strong>
            </li>
            <li>
              <span>Average</span>
              <strong>{teachers.length ? Math.round(teacherTotal / teachers.length) : 0}</strong>
            </li>
          </ul>
          <ol className="rmadd-teachers rmadd-teachers--all">
            {teachers.map((t, i) => (
              <li key={t.id} className="rmadd-teacher">
                <span className={`rmadd-rank ${medal[i] || ''}`}>{i + 1}</span>
                <div className="rmadd-teacher-body">
                  <strong>{t.name}</strong>
                  <span>
                    {t.count} class{t.count === 1 ? '' : 'es'} · {teacherTotal ? ((t.count / teacherTotal) * 100).toFixed(1) : 0}%
                  </span>
                  <span className="rmadd-share" aria-hidden="true">
                    <span style={{ width: `${teacherTotal ? (t.count / teacherTotal) * 100 : 0}%` }} />
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </DashModal>
      )}

      {/* ---------- Demo details ---------- */}
      {demoId && <DemoModal demoId={demoId} onClose={() => setDemoId(null)} onChanged={refresh} />}
    </>
  );
};

export default DashOverview;